import { OPERATION_IDS } from '../routes/ids.js';
import { ORGANIZATION_PERMISSIONS_LIST } from '../../operations/operation-ids.js';
import { MAINTENANCE_DELEGATION_CREATE_FORM, MAINTENANCE_DELEGATION_REVOKE_FORM, } from './maintenance-delegation-descriptors.js';
import { USER_CREATE_FORM as BASE_USER_CREATE_FORM, USER_EDIT_FORM as BASE_USER_EDIT_FORM, USER_VIEW_FORM as BASE_USER_VIEW_FORM, ROLE_CREATE_FORM as BASE_ROLE_CREATE_FORM, ROLE_EDIT_FORM as BASE_ROLE_EDIT_FORM, ROLE_VIEW_FORM as BASE_ROLE_VIEW_FORM, PLATFORM_ORGANIZATION_CREATE_FORM as BASE_PLATFORM_ORGANIZATION_CREATE_FORM, PLATFORM_ORGANIZATION_EDIT_FORM as BASE_PLATFORM_ORGANIZATION_EDIT_FORM, PLATFORM_ORGANIZATION_VIEW_FORM as BASE_PLATFORM_ORGANIZATION_VIEW_FORM, CLIENT_ACCOUNT_CREATE_FORM as BASE_CLIENT_ACCOUNT_CREATE_FORM, CLIENT_ACCOUNT_EDIT_FORM as BASE_CLIENT_ACCOUNT_EDIT_FORM, CLIENT_ACCOUNT_VIEW_FORM as BASE_CLIENT_ACCOUNT_VIEW_FORM, PORTFOLIO_CREATE_FORM as BASE_PORTFOLIO_CREATE_FORM, PORTFOLIO_EDIT_FORM as BASE_PORTFOLIO_EDIT_FORM, PORTFOLIO_VIEW_FORM as BASE_PORTFOLIO_VIEW_FORM, EXPERIENCE_FORM_REGISTRY as BASE_FORM_REGISTRY, } from './form-registry.js';
const USER_BRANCH_FIELD_ID = 'field.user.branchId';
const USER_ROLES_FIELD_ID = 'field.user.roles';
const ROLE_PERMISSIONS_FIELD_ID = 'field.role.permissions';
const fieldId = (value) => value;
const tk = (value) => value;
const ruleId = (value) => value;
function apiOptions(field, operationId, valuePath, labelPath) {
    return {
        ...field,
        options: undefined,
        optionSource: {
            adapterId: 'api',
            operationId,
            valuePath,
            labelPath,
            cachePolicy: { ttlSeconds: 60 },
            allowSearch: true,
            minimumSearchLength: 0,
        },
    };
}
function configureUserFields(fields) {
    return fields.map((field) => {
        if (String(field.id) === USER_ROLES_FIELD_ID) {
            return apiOptions(field, OPERATION_IDS.ORGANIZATION_ROLES_LIST, 'id', 'name');
        }
        if (String(field.id) === USER_BRANCH_FIELD_ID) {
            return apiOptions(field, OPERATION_IDS.ORGANIZATION_BRANCHES_LIST, 'id', 'name');
        }
        return field;
    });
}
function configureRoleFields(fields) {
    return fields.map((field) => String(field.id) === ROLE_PERMISSIONS_FIELD_ID
        ? apiOptions(field, ORGANIZATION_PERMISSIONS_LIST, 'value', 'label')
        : field);
}
/**
 * Currency remains a text control until a backend-owned ISO-4217 directory is
 * available. This mirrors the server contract exactly instead of hard-coding a
 * partial currency list in the browser-facing descriptor.
 */
function currencyField(field) {
    if (field.name !== 'baseCurrency')
        return field;
    const existingRules = field.validation?.rules ?? [];
    const withoutDuplicatePattern = existingRules.filter((rule) => String(rule.ruleId) !== 'pattern');
    return {
        ...field,
        validation: {
            ...field.validation,
            rules: [
                ...withoutDuplicatePattern,
                {
                    ruleId: ruleId('pattern'),
                    params: { pattern: '^[A-Z]{3}$' },
                    messageKey: tk('validation.pattern'),
                },
            ],
            validateWhen: field.validation?.validateWhen ?? 'blur',
        },
    };
}
function configureCurrencyFields(fields) {
    return fields.map(currencyField);
}
function filingProfileField(id, name, options = {}) {
    const rules = [
        ...(options.required
            ? [{ ruleId: ruleId('required'), messageKey: tk('validation.required') }]
            : []),
        ...(options.pattern
            ? [
                {
                    ruleId: ruleId('pattern'),
                    params: { pattern: options.pattern },
                    messageKey: tk('validation.pattern'),
                },
            ]
            : []),
    ];
    return {
        id: fieldId(id),
        name,
        labelKey: tk(id),
        fieldType: options.fieldType ?? 'text',
        layout: { md: 12 },
        value: {
            hiddenValuePolicy: options.sensitive ? 'clear' : 'unregister',
            submitBehavior: options.outputOnly ? 'exclude' : 'include',
            outputOnly: options.outputOnly,
            clearWhenHidden: options.sensitive,
        },
        validation: rules.length > 0 ? { rules, validateWhen: 'blur' } : undefined,
        sensitive: options.sensitive
            ? { payloadBehavior: options.outputOnly ? 'omit' : 'submit', displayBehavior: 'mask' }
            : undefined,
        display: options.outputOnly ? { readonlySubmission: 'exclude' } : undefined,
    };
}
function withFields(base, fields) {
    return {
        ...base,
        fields,
        sections: undefined,
        submission: {
            ...base.submission,
            includeFields: fields
                .filter((field) => field.value?.submitBehavior !== 'exclude' && !field.value?.outputOnly)
                .map((field) => field.id),
            excludeFields: fields
                .filter((field) => field.value?.submitBehavior === 'exclude' || field.value?.outputOnly)
                .map((field) => field.id),
        },
    };
}
export const USER_CREATE_FORM = {
    ...BASE_USER_CREATE_FORM,
    fields: configureUserFields(BASE_USER_CREATE_FORM.fields),
};
/** Role branch changes are access changes and use the canonical user update command. */
export const USER_EDIT_FORM = {
    ...BASE_USER_EDIT_FORM,
    fields: configureUserFields(BASE_USER_EDIT_FORM.fields),
};
export const USER_VIEW_FORM = {
    ...BASE_USER_VIEW_FORM,
    fields: configureUserFields(BASE_USER_VIEW_FORM.fields),
};
export const ROLE_CREATE_FORM = {
    ...BASE_ROLE_CREATE_FORM,
    fields: configureRoleFields(BASE_ROLE_CREATE_FORM.fields),
};
export const ROLE_EDIT_FORM = {
    ...BASE_ROLE_EDIT_FORM,
    fields: configureRoleFields(BASE_ROLE_EDIT_FORM.fields),
};
export const ROLE_VIEW_FORM = {
    ...BASE_ROLE_VIEW_FORM,
    fields: configureRoleFields(BASE_ROLE_VIEW_FORM.fields),
};
const createBaseFields = BASE_PLATFORM_ORGANIZATION_CREATE_FORM.fields;
export const PLATFORM_ORGANIZATION_CREATE_FORM = withFields(BASE_PLATFORM_ORGANIZATION_CREATE_FORM, configureCurrencyFields(createBaseFields));
const editBaseFields = BASE_PLATFORM_ORGANIZATION_EDIT_FORM.fields;
export const PLATFORM_ORGANIZATION_EDIT_FORM = withFields(BASE_PLATFORM_ORGANIZATION_EDIT_FORM, [
    ...editBaseFields.filter((field) => field.name === 'expectedVersion'),
    ...editBaseFields.filter((field) => field.name === 'name'),
    filingProfileField('field.organization.legalName', 'legalName', { required: true }),
    filingProfileField('field.organization.panNumber', 'panNumber', {
        pattern: '^\\d{9}$',
        sensitive: true,
    }),
    filingProfileField('field.organization.sebonRegistrationNumber', 'sebonRegistrationNumber'),
    ...configureCurrencyFields(editBaseFields.filter((field) => field.name === 'baseCurrency')),
]);
export const PLATFORM_ORGANIZATION_VIEW_FORM = withFields(BASE_PLATFORM_ORGANIZATION_VIEW_FORM, [
    ...BASE_PLATFORM_ORGANIZATION_VIEW_FORM.fields
        .filter((field) => field.name === 'code' ||
        field.name === 'name' ||
        field.name === 'legalName' ||
        field.name === 'jurisdiction')
        .map((field) => ({
        ...currencyField(field),
        value: { ...field.value, submitBehavior: 'exclude', outputOnly: true },
        display: { ...field.display, readonlySubmission: 'exclude' },
    })),
    filingProfileField('field.organization.regulatoryProfileReady', 'regulatoryProfileReady', {
        outputOnly: true,
        fieldType: 'switch',
    }),
    filingProfileField('field.organization.status', 'status', { outputOnly: true }),
    filingProfileField('field.organization.assignedModules', 'assignedModules', { outputOnly: true }),
    filingProfileField('field.organization.branchCount', 'branchCount', { outputOnly: true }),
]);
export const CLIENT_ACCOUNT_CREATE_FORM = {
    ...BASE_CLIENT_ACCOUNT_CREATE_FORM,
    fields: configureCurrencyFields(BASE_CLIENT_ACCOUNT_CREATE_FORM.fields),
};
export const CLIENT_ACCOUNT_EDIT_FORM = {
    ...BASE_CLIENT_ACCOUNT_EDIT_FORM,
    fields: configureCurrencyFields(BASE_CLIENT_ACCOUNT_EDIT_FORM.fields),
};
export const CLIENT_ACCOUNT_VIEW_FORM = {
    ...BASE_CLIENT_ACCOUNT_VIEW_FORM,
    fields: configureCurrencyFields(BASE_CLIENT_ACCOUNT_VIEW_FORM.fields),
};
export const PORTFOLIO_CREATE_FORM = {
    ...BASE_PORTFOLIO_CREATE_FORM,
    fields: configureCurrencyFields(BASE_PORTFOLIO_CREATE_FORM.fields),
};
export const PORTFOLIO_EDIT_FORM = {
    ...BASE_PORTFOLIO_EDIT_FORM,
    fields: configureCurrencyFields(BASE_PORTFOLIO_EDIT_FORM.fields),
};
export const PORTFOLIO_VIEW_FORM = {
    ...BASE_PORTFOLIO_VIEW_FORM,
    fields: configureCurrencyFields(BASE_PORTFOLIO_VIEW_FORM.fields),
};
export { MAINTENANCE_DELEGATION_CREATE_FORM, MAINTENANCE_DELEGATION_REVOKE_FORM };
export const EXPERIENCE_FORM_REGISTRY = Object.freeze({
    ...BASE_FORM_REGISTRY,
    [String(USER_CREATE_FORM.id)]: USER_CREATE_FORM,
    [String(USER_EDIT_FORM.id)]: USER_EDIT_FORM,
    [String(USER_VIEW_FORM.id)]: USER_VIEW_FORM,
    [String(ROLE_CREATE_FORM.id)]: ROLE_CREATE_FORM,
    [String(ROLE_EDIT_FORM.id)]: ROLE_EDIT_FORM,
    [String(ROLE_VIEW_FORM.id)]: ROLE_VIEW_FORM,
    [String(PLATFORM_ORGANIZATION_CREATE_FORM.id)]: PLATFORM_ORGANIZATION_CREATE_FORM,
    [String(PLATFORM_ORGANIZATION_EDIT_FORM.id)]: PLATFORM_ORGANIZATION_EDIT_FORM,
    [String(PLATFORM_ORGANIZATION_VIEW_FORM.id)]: PLATFORM_ORGANIZATION_VIEW_FORM,
    [String(CLIENT_ACCOUNT_CREATE_FORM.id)]: CLIENT_ACCOUNT_CREATE_FORM,
    [String(CLIENT_ACCOUNT_EDIT_FORM.id)]: CLIENT_ACCOUNT_EDIT_FORM,
    [String(CLIENT_ACCOUNT_VIEW_FORM.id)]: CLIENT_ACCOUNT_VIEW_FORM,
    [String(PORTFOLIO_CREATE_FORM.id)]: PORTFOLIO_CREATE_FORM,
    [String(PORTFOLIO_EDIT_FORM.id)]: PORTFOLIO_EDIT_FORM,
    [String(PORTFOLIO_VIEW_FORM.id)]: PORTFOLIO_VIEW_FORM,
    [String(MAINTENANCE_DELEGATION_CREATE_FORM.id)]: MAINTENANCE_DELEGATION_CREATE_FORM,
    [String(MAINTENANCE_DELEGATION_REVOKE_FORM.id)]: MAINTENANCE_DELEGATION_REVOKE_FORM,
});
export function getExperienceFormDescriptor(id) {
    return EXPERIENCE_FORM_REGISTRY[id];
}
